import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

const camiones = [
  {
    nombre: "CAMIÓN VOLVO FH GLOBETROTTER CAMA BAJA",
    imagen: "https://storage.readdy-site.link/project_files/5397651f-fa4a-4daf-a2cb-668ede89c5b0/06667a81-0ecc-4837-884e-6a7cfb5872b1_6b1f135d-5685-4bb5-8f8f-90eb38fc149c.jpg?v=a671bfe9202dc7ae6bfdfd8226d3efe9",
    caracteristicas: [
      "Camión de alta capacidad para transporte de maquinaria pesada",
      "Capacidad de hasta 60 toneladas",
      "Semirremolque cama baja de largo alcance",
      "Patente: KR-ZX-99",
      "Mantención programada de equipo",
      "Ideal para traslado de grúas, excavadoras y equipos industriales",
    ],
  },
  {
    nombre: "CAMIÓN PLUMA IVECO TECTOR 260E28",
    imagen: "https://storage.readdy-site.link/project_files/5397651f-fa4a-4daf-a2cb-668ede89c5b0/89b14a9d-ea09-4426-9336-dad9c18dfcb7_WhatsApp-Image-2026-04-14-at-10.36.44-AM-4.jpeg?v=06eab6ee5b8b97090bd517d75ff8e566",
    caracteristicas: [
      "Camión pluma con grúa PM de alta potencia",
      "Capacidad de carga: 11,5 toneladas",
      "Patente: FT-PF-66",
      "Mantención programada de equipo",
      "Grúa PM - Alcance vertical: 18 mts / Horizontal: 14 mts",
      "Incluye telemando inalámbrico",
      "Plataforma para carga y descarga de materiales de construcción",
    ],
  },
];

export default function AreaTransporte() {
  const sectionsRef = useRef<(HTMLElement | null)[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("opacity-100", "translate-y-0");
            entry.target.classList.remove("opacity-0", "translate-y-8");
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -50px 0px" }
    );
    sectionsRef.current.forEach((el) => {
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const setRef = (index: number) => (el: HTMLElement | null) => {
    sectionsRef.current[index] = el;
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="relative h-[420px] md:h-[520px] overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=professional%20heavy%20transport%20truck%20fleet%20parked%20in%20an%20industrial%20yard%20at%20dusk%2C%20large%20crane%20trucks%20and%20flatbed%20trucks%20aligned%2C%20dramatic%20cinematic%20lighting%20with%20warm%20orange%20tones%2C%20industrial%20port%20or%20mining%20facility%20background%2C%20clean%20and%20powerful%20composition%2C%20high%20resolution%20commercial%20photography%20style&width=1920&height=600&seq=transporte-hero-v2&orientation=landscape"
            alt="Área Transporte"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#1C2B38]/75 via-[#1C2B38]/55 to-[#1C2B38]/75"></div>
        </div>
        <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-6">
          <p className="text-orange-400 text-sm font-semibold uppercase tracking-widest mb-3">
            Charles Juárez
          </p>
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-4 tracking-tight">
            ÁREA TRANSPORTE
          </h1>
          <div className="w-24 h-1 bg-orange-500 mx-auto rounded-full mb-6"></div>
          <p className="text-white/80 text-lg max-w-xl">
            Flota de camiones especializados para traslado de maquinaria pesada y cargas de gran tonelaje
          </p>
        </div>
      </section>

      {/* Intro */}
      <section
        ref={setRef(0)}
        className="py-14 px-6 opacity-0 translate-y-8 transition-all duration-700"
      >
        <div className="max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-100 text-orange-600 rounded-full text-sm font-semibold mb-6">
            <i className="ri-truck-line"></i>
            <span>Flota Propia</span>
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-[#1C2B38] mb-4">
            Transporte Especializado de Maquinaria
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto leading-relaxed">
            Contamos con una flota de camiones propios con mantención programada, equipados con grúas pluma de alto alcance y capacidad para trasladar maquinaria pesada de hasta 60 toneladas.
          </p>
        </div>
      </section>

      {/* Flota de Camiones */}
      <section
        ref={setRef(1)}
        className="pb-20 px-6 opacity-0 translate-y-8 transition-all duration-700"
      >
        <div className="max-w-6xl mx-auto">
          <div className="grid sm:grid-cols-2 gap-8">
            {camiones.map((camion, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl overflow-hidden border border-gray-100 hover:border-orange-200 transition-all duration-300 group"
              >
                {/* Imagen */}
                <div className="w-full bg-gray-50 overflow-hidden flex items-center justify-center">
                  <img
                    src={camion.imagen}
                    alt={camion.nombre}
                    className="w-full object-contain group-hover:scale-105 transition-transform duration-500"
                  />
                </div>

                {/* Contenido */}
                <div className="p-6">
                  <h3 className="text-base font-bold text-orange-500 mb-4 uppercase tracking-wide">
                    {camion.nombre}
                  </h3>
                  <div className="w-10 h-0.5 bg-orange-300 mb-4 rounded-full"></div>
                  <p className="text-sm font-semibold text-[#1C2B38] mb-3">Características:</p>
                  <ul className="space-y-2">
                    {camion.caracteristicas.map((c, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                        <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <i className="ri-arrow-right-s-line text-orange-500"></i>
                        </div>
                        <span>{c}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section
        ref={setRef(2)}
        className="py-16 px-6 bg-gray-50 opacity-0 translate-y-8 transition-all duration-700"
      >
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { icon: "ri-truck-line", value: "2", label: "Camiones en Flota" },
              { icon: "ri-weight-line", value: "60 T", label: "Capacidad Máxima" },
              { icon: "ri-arrow-up-line", value: "18 mts", label: "Alcance Vertical" },
              { icon: "ri-arrow-right-line", value: "14 mts", label: "Alcance Horizontal" },
            ].map((stat, i) => (
              <div key={i} className="bg-white rounded-xl p-6 text-center border border-gray-100">
                <div className="w-12 h-12 flex items-center justify-center bg-orange-100 rounded-xl mx-auto mb-3">
                  <i className={`${stat.icon} text-orange-500 text-xl`}></i>
                </div>
                <p className="text-2xl font-bold text-[#1C2B38] mb-1">{stat.value}</p>
                <p className="text-xs text-gray-500">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-20 px-6 bg-[#1C2B38]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl md:text-4xl font-bold text-white mb-4">
            ¿Necesitas trasladar maquinaria o equipos?
          </h2>
          <p className="text-gray-300 mb-8 text-lg">
            Contáctanos y te cotizamos el traslado con nuestra flota especializada
          </p>
          <Link
            to="/contacto"
            className="inline-flex items-center gap-2 bg-orange-500 text-white px-8 py-4 rounded-full font-semibold hover:bg-orange-600 transition-colors duration-300 cursor-pointer whitespace-nowrap"
          >
            <span>Solicitar Cotización</span>
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
